/**
 * Coups de pouce — Attendu 902 « Enchaîner des opérations ».
 * Quatre cartes, six fiches-pages, sept generator_key.
 *
 * ─── Forme, reprise des revues 900 et 901 ──────────────────────────────────
 *   `court`  → l'idée-clé, une phrase (rendue en gras avec 💡)
 *   `lignes` → DEUX étapes, numérotées par la sheet
 *   `svg`    → le schéma seul, sans texte de règle
 * Seuls `court` et `lignes` sont lus à voix haute par CoupDePouceSheet : une
 * fiche dont la règle vit dans le SVG est MUETTE. Le schéma garde son
 * `aria-label` pour les lecteurs d'écran.
 *
 * ─── Ce qui est partagé, et pourquoi ───────────────────────────────────────
 * Le partage suit le TRAVAIL RÉEL demandé, pas la carte :
 *   - `effectuer_texte` s'arrête au résultat → PAGE_PRIORITES seule ;
 *     `effectuer_texte_trou_saisie` doit en plus tenir la réécriture ligne par
 *     ligne → la même page, plus PAGE_CROCHETS.
 *   - les deux formats de « programme » demandent le même parcours (le QCM
 *     n'affiche que le nombre final, mais il faut l'avoir calculé) → fiche
 *     entière commune.
 *   - `verifier_qcm_unique` ne pose qu'une question, « quelle opération en
 *     dernier » → PAGE_DERNIERE seule ; `verifier_tableau_vf` y ajoute « par
 *     quoi peut-on commencer » → PAGE_COMMENCER en page 1.
 * Les pages communes le sont PAR IDENTITÉ d'objet — un seul littéral, référencé
 * deux fois. Les `generator_key` étant distincts, aucun routage par `sousType`
 * n'est nécessaire.
 *
 * ─── Aucune fiche n'est du pur rappel ──────────────────────────────────────
 * La règle acquise, il reste à chaque fois du travail : appliquer la priorité à
 * une expression inconnue, réécrire quatre lignes, suivre trois consignes,
 * chaîner trois étiquettes. On écrit donc une entrée pour les sept clés.
 *
 * ─── Les valeurs restent HORS des plages de tirage ─────────────────────────
 * Sans quoi une fiche servirait la réponse d'un exercice ouvert. Chaque valeur
 * est justifiée générateur en main, ET vérifiée par tirage : la sonde de revue
 * cherchait les empreintes de chaque fiche dans 5000 énoncés de sa propre clé.
 * Trois collisions y ont été prises (revue 2026-08-02) et corrigées ici :
 *   - `7 + 4 × 5` : G3 s'écrit toujours `a + b × c − d`, avec un terme final,
 *     et son facteur `b` vaut 12, 15, 25 ou 35, jamais 4 ;
 *   - `(7 + 4) × 5` : G6 parenthèse toujours SES DEUX facteurs, `(a+b) × (c−d)` ;
 *   - `2,5 + 1,5 = 4` : G4 force la somme intérieure à ne jamais tomber juste
 *     (`s10 % 10 !== 0`), une somme entière y est inatteignable. Le crochet vaut
 *     `40 − [20 − …]` et non `30 − [12 − …]` : le `b` de G4 plafonne à 16, la
 *     première écriture sortait 2 fois sur 5000 en simple préfixe ;
 *   - le programme part de `20`, alors que `n` est tiré dans [3..12] ;
 *   - l'ordonnancement montre `24 + 16 = 40` puis `40 × 3 = 120`. La version
 *     initiale, `11 + 9 = 20` puis `20 × 3 = 60`, sortait 22 et 16 fois sur
 *     5000 : le gabarit B tire `a ∈ [3..15]`, `b ∈ [2..12]`, `c ∈ [2..6]`, ces
 *     deux étiquettes étaient donc de vraies cartes d'exercice. Avec 24 et 16,
 *     la somme 40 dépasse le maximum atteignable (27) et rien ne collide ;
 *   - `(18 − 6) × 2 + 42 ÷ 6` : `k ∈ [3..6]` donc le facteur 2 est hors plage,
 *     et les deux termes de la parenthèse du générateur sont TOUJOURS décimaux.
 *     La division est `42 ÷ 6` et non `30 ÷ 5`, qui sortait 274 fois sur 5000 :
 *     le générateur tire `q ∈ [3..5]` et `p = q × t ≤ 40` ;
 *   - `5 × 3 + 8` et `5 × (3 + 8)` : formes absentes des 4 structures de la
 *     carte « vérifier », qui parenthèsent toutes une différence décimale.
 * La lettre **K** est hors de `LETTRES_CALC` (A à F) : aucune fiche ne peut
 * être confondue avec l'énoncé qu'elle accompagne.
 *
 * ⚠ Géométrie : les six figures passent une sonde qui reconstitue la boîte de
 * chaque <text> et vérifie qu'elle tient dans le viewBox et ne recouvre ni le
 * bouton « Revoir » ni un autre texte. Toute retouche est à re-mesurer.
 */

/**
 * Étape d'apparition : le groupe reste transparent jusqu'à `p` (fraction du
 * cycle), monte en 3 % puis GÈLE. Tous les groupes d'une figure partagent le
 * même `begin`, donc un clic sur le bouton rejoue toute la cascade.
 */
const apparition = (p, btn, duree = '7.6s') =>
    `<animate attributeName="opacity" values="0;0;1;1" ` +
    `keyTimes="0;${p.toFixed(2)};${(p + 0.03).toFixed(2)};1" ` +
    `dur="${duree}" begin="0s;${btn}.click" fill="freeze"/>`;

/** Groupe animé. opacity="1" en attribut = repli si SMIL est indisponible. */
const etape = (p, btn, contenu) =>
    `<g class="anim" opacity="1">${apparition(p, btn)}${contenu}</g>`;

const bouton = (id, x, y) =>
    `<g id="${id}" style="cursor:pointer">` +
    `<title>Rejouer l'animation</title>` +
    `<rect x="${x}" y="${y}" width="76" height="21" rx="10.5" fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.2"/>` +
    `<text x="${x + 38}" y="${y + 15}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">↻ Revoir</text>` +
    `</g>`;

// Le seul CSS restant : respecter prefers-reduced-motion. En cas d'échec de la
// règle, l'élève voit l'animation, jamais une figure vide.
const STYLE_REDUCED = '<style>@media (prefers-reduced-motion: reduce) { .anim { opacity: 1 !important; } }</style>';

const BLEU = '#1E3A8A', ORANGE = '#f97316', ARDOISE = '#334155', ENCRE = '#0f172a';
const GRIS = '#94a3b8', VERT = '#16a34a', ROUGE = '#dc2626';

/** Filet de séparation entre les deux scènes d'une même figure. */
const filet = (y) => `<line x1="14" y1="${y}" x2="286" y2="${y}" stroke="#e2e8f0" stroke-width="1.2"/>`;

const titre = (txt) => `<text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">${txt}</text>`;

const coche = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${VERT}">✓</text>`;
const croix = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${ROUGE}">✗</text>`;

/** Flèche horizontale, légende au-dessus. */
const fleche = (x1, x2, y, label = '', couleur = ORANGE) =>
    `<line x1="${x1}" y1="${y}" x2="${x2 - 6}" y2="${y}" stroke="${couleur}" stroke-width="1.6"/>` +
    `<path d="M ${x2} ${y} L ${x2 - 7} ${y - 4} L ${x2 - 7} ${y + 4} Z" fill="${couleur}"/>` +
    (label
        ? `<text x="${(x1 + x2) / 2}" y="${y - 7}" text-anchor="middle" font-size="10" font-weight="bold" fill="${couleur}">${label}</text>`
        : '');

/** Flèche verticale — le report d'un résultat vers l'étape suivante. */
const flecheV = (x, y1, y2, couleur = ORANGE) =>
    `<line x1="${x}" y1="${y1}" x2="${x}" y2="${y2 - 6}" stroke="${couleur}" stroke-width="1.6"/>` +
    `<path d="M ${x} ${y2} L ${x - 4} ${y2 - 7} L ${x + 4} ${y2 - 7} Z" fill="${couleur}"/>`;

/** Cartouche de fond d'une étiquette d'étape. */
const bande = (y, h = 34) =>
    `<rect x="14" y="${y}" width="272" height="${h}" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.2"/>`;

/** Pastille de statut à droite d'une bande (« étape », « réponse »). */
const pastille = (x, y, label, fond, trait, encre) =>
    `<rect x="${x}" y="${y}" width="58" height="18" rx="9" fill="${fond}" stroke="${trait}" stroke-width="1.1"/>` +
    `<text x="${x + 29}" y="${y + 13}" text-anchor="middle" font-size="10" font-weight="bold" fill="${encre}">${label}</text>`;

// ═══════════════════════════════════════════════════════════
// Fiche 1 — Effectuer un calcul
// ═══════════════════════════════════════════════════════════

const BTN_A = 'cp902rejouerA', BTN_B = 'cp902rejouerB';

/**
 * Les mêmes trois nombres, deux chemins, puis la version parenthésée. La
 * troisième scène n'est pas décorative : la moitié des gabarits de la carte
 * PORTENT des parenthèses, et c'est elle qui explique pourquoi 55 n'est pas
 * « le mauvais résultat » mais « le résultat d'un autre calcul ».
 */
const SVG_PRIORITES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 202" width="290" height="195" role="img"
     aria-label="Le calcul 7 plus 4 fois 5, traité de deux façons. En haut, la bonne : on calcule d'abord 4 fois 5 égale 20, puis 7 plus 20 égale 27, coche verte. Au milieu, l'erreur : en allant de gauche à droite on calcule 7 plus 4 égale 11, puis 11 fois 5 égale 55, croix rouge. En bas, une remarque : avec des parenthèses, 7 plus 4 entre parenthèses fois 5 fait bien 55, ce sont les parenthèses qui changent l'ordre. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Qui passe en premier ?')}
  ${bouton(BTN_A, 210, 4)}
  ${etape(0.05, BTN_A,
    `<text x="20" y="54" font-size="13.5" font-weight="bold" fill="${ENCRE}">7 + <tspan fill="${ORANGE}">4 × 5</tspan></text>` +
    `<text x="20" y="72" font-size="9.5" font-weight="bold" fill="${ORANGE}">la multiplication d'abord</text>`)}
  ${etape(0.16, BTN_A, fleche(148, 176, 50))}
  ${etape(0.24, BTN_A, `<text x="184" y="54" font-size="13.5" font-weight="bold" fill="${ENCRE}">7 + 20</text>`)}
  ${etape(0.32, BTN_A,
    `<text x="238" y="54" font-size="13.5" font-weight="bold" fill="${VERT}">27</text>` + coche(264, 55))}
  ${etape(0.40, BTN_A, filet(86))}
  ${etape(0.46, BTN_A,
    `<text x="20" y="120" font-size="13.5" font-weight="bold" fill="${ENCRE}"><tspan fill="${ROUGE}">7 + 4</tspan> × 5</text>` +
    `<text x="20" y="138" font-size="9.5" font-weight="bold" fill="${ROUGE}">de gauche à droite</text>`)}
  ${etape(0.56, BTN_A, fleche(148, 176, 116, '', GRIS))}
  ${etape(0.64, BTN_A, `<text x="184" y="120" font-size="13.5" font-weight="bold" fill="${ARDOISE}">11 × 5</text>`)}
  ${etape(0.72, BTN_A,
    `<text x="238" y="120" font-size="13.5" font-weight="bold" fill="${ROUGE}">55</text>` + croix(264, 121))}
  ${etape(0.80, BTN_A, filet(152))}
  ${etape(0.86, BTN_A,
    `<text x="20" y="176" font-size="13" font-weight="bold" fill="${BLEU}">(7 + 4) × 5 = 55</text>`)}
  ${etape(0.93, BTN_A,
    `<text x="20" y="194" font-size="10" fill="${GRIS}">ce sont les parenthèses qui changent l'ordre</text>`)}
</svg>`.trim();

/**
 * La convention de la FEUILLE, celle que le format à trous demande : à chaque
 * ligne on réécrit TOUT le calcul, le morceau prioritaire remplacé par sa
 * valeur. Le nombre qui vient d'apparaître est en orange, celui qu'on va
 * calculer ensuite est encadré.
 */
const SVG_CROCHETS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 168" width="290" height="162" role="img"
     aria-label="Le calcul K égale 40 moins crochet 20 moins parenthèse 2 virgule 5 plus 1 virgule 5, réécrit ligne par ligne. Première ligne, on calcule la parenthèse la plus intérieure : 2 virgule 5 plus 1 virgule 5 font 4. Deuxième ligne, K égale 40 moins crochet 20 moins 4 : on calcule le crochet, 20 moins 4 font 16. Troisième ligne, K égale 40 moins 16. Dernière ligne, K égale 24, coche verte. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Du dedans vers le dehors')}
  ${bouton(BTN_B, 210, 4)}
  ${etape(0.05, BTN_B,
    `<text x="14" y="52" font-size="12.5" font-weight="bold" fill="${ENCRE}">K = 40 − [20 − <tspan fill="${ORANGE}">(2,5 + 1,5)</tspan>]</text>`)}
  ${etape(0.18, BTN_B,
    `<text x="14" y="68" font-size="9.5" font-weight="bold" fill="${ORANGE}">la parenthèse la plus intérieure</text>`)}
  ${etape(0.32, BTN_B, flecheV(24, 74, 88))}
  ${etape(0.40, BTN_B,
    `<text x="38" y="96" font-size="12.5" font-weight="bold" fill="${ENCRE}">K = 40 − [20 − <tspan fill="${ORANGE}">4</tspan>]</text>`)}
  ${etape(0.56, BTN_B, flecheV(24, 102, 122))}
  ${etape(0.64, BTN_B,
    `<text x="38" y="130" font-size="12.5" font-weight="bold" fill="${ENCRE}">K = 40 − <tspan fill="${ORANGE}">16</tspan></text>`)}
  ${etape(0.78, BTN_B,
    `<text x="160" y="130" font-size="9.5" fill="${GRIS}">le crochet est fait</text>`)}
  ${etape(0.88, BTN_B,
    `<text x="38" y="158" font-size="14" font-weight="bold" fill="${VERT}">K = 24</text>` + coche(112, 159))}
</svg>`.trim();

const PAGE_PRIORITES = {
    titre: 'L\'ordre des priorités',
    court: 'Je fais les parenthèses d\'abord, puis les × et ÷, et seulement après les + et −.',
    lignes: [
        '**7 + 4 × 5** : je calcule 4 × 5 = 20, puis 7 + 20 = **27**.',
        'De gauche à droite je trouverais 55 : c\'est l\'erreur à éviter.',
    ],
    svg: SVG_PRIORITES,
};

/** La saisie s'arrête au résultat : une seule page. */
const FICHE_EFFECTUER_SAISIE = { pages: [PAGE_PRIORITES] };

const FICHE_EFFECTUER_TROUS = {
    pages: [
        PAGE_PRIORITES,
        {
            titre: 'Du plus profond vers l\'extérieur',
            court: 'Dans des crochets, je commence par la parenthèse la plus intérieure.',
            lignes: [
                '**K = 40 − [20 − (2,5 + 1,5)]** : d\'abord 2,5 + 1,5 = **4**.',
                'Je réécris tout le calcul à chaque ligne, en remplaçant ce que je viens de faire.',
            ],
            svg: SVG_CROCHETS,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 2 — Programme de calcul
// ═══════════════════════════════════════════════════════════

const BTN_C = 'cp902rejouerC', BTN_D = 'cp902rejouerD';

/**
 * Une consigne à gauche, son calcul à droite. Le nombre REPORTÉ est en orange
 * aux deux endroits où il apparaît : c'est le seul lien visuel entre deux
 * bandes, et c'est exactement le geste que le format à trous demande.
 */
const bandeConsigne = (y, consigne, avant, suite, apres) =>
    bande(y) +
    `<text x="26" y="${y + 21}" font-size="10.5" fill="${BLEU}">${consigne}</text>` +
    `<text x="150" y="${y + 22}" font-size="13" font-weight="bold" fill="${ENCRE}">` +
    (avant ? `<tspan fill="${ORANGE}">${avant}</tspan>` : '') +
    `${suite}<tspan fill="${ORANGE}">${apres}</tspan></text>`;

const SVG_PROGRAMME = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 190" width="290" height="184" role="img"
     aria-label="Un programme de calcul appliqué pas à pas. On part du nombre 20. Première consigne, multiplie par 3 : 20 fois 3 égale 60. Deuxième consigne, ajoute 6 : 60 plus 6 égale 66. Troisième consigne, divise par 2 : 66 divisé par 2 égale 33. Chaque résultat est repris en orange au début de l'étape suivante. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Une étape à la fois')}
  ${bouton(BTN_C, 210, 4)}
  ${etape(0.05, BTN_C,
    `<text x="14" y="46" font-size="12" fill="${ARDOISE}">Pars du nombre <tspan font-size="13.5" font-weight="bold" fill="${ORANGE}">20</tspan></text>`)}
  ${etape(0.20, BTN_C, bandeConsigne(56, 'multiplie par 3', '20', ' × 3 = ', '60'))}
  ${etape(0.44, BTN_C, bandeConsigne(98, 'ajoute 6', '60', ' + 6 = ', '66'))}
  ${etape(0.68, BTN_C, bandeConsigne(140, 'divise par 2', '66', ' ÷ 2 = ', '33'))}
</svg>`.trim();

/**
 * Pas de croix rouge sur les deux premières lignes : leurs calculs sont JUSTES,
 * une croix laisserait croire le contraire. Ce sont des pastilles de statut qui
 * distinguent l'étape de la réponse — la misconception visée est « je rends un
 * résultat intermédiaire », pas « je calcule mal ». Les deux distracteurs du
 * QCM de cette carte sont précisément 60 et 66.
 */
const SVG_JUSQU_AU_BOUT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 196" width="290" height="190" role="img"
     aria-label="Les trois résultats du programme, avec leur statut. 20 fois 3 égale 60 : c'est une étape. 60 plus 6 égale 66 : c'est une étape. 66 divisé par 2 égale 33 : c'est la réponse, coche verte. En bas, un rappel : il y avait trois consignes, il faut donc trois étapes. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Aller jusqu\'au bout')}
  ${bouton(BTN_D, 210, 4)}
  ${etape(0.05, BTN_D,
    bande(36) +
    `<text x="26" y="58" font-size="13" font-weight="bold" fill="${ARDOISE}">20 × 3 = 60</text>` +
    pastille(206, 44, 'étape', '#f1f5f9', GRIS, ARDOISE))}
  ${etape(0.26, BTN_D,
    bande(80) +
    `<text x="26" y="102" font-size="13" font-weight="bold" fill="${ARDOISE}">60 + 6 = 66</text>` +
    pastille(206, 88, 'étape', '#f1f5f9', GRIS, ARDOISE))}
  ${etape(0.50, BTN_D,
    bande(124) +
    `<text x="26" y="146" font-size="13" font-weight="bold" fill="${VERT}">66 ÷ 2 = 33</text>` +
    pastille(206, 132, 'réponse', '#dcfce7', VERT, '#15803d'))}
  ${etape(0.76, BTN_D,
    `<text x="14" y="184" font-size="10.5" fill="${GRIS}">3 consignes dans l'énoncé, donc 3 étapes à faire</text>`)}
</svg>`.trim();

const FICHE_PROGRAMME = {
    pages: [
        {
            titre: 'Une étape à la fois',
            court: 'Je suis les consignes dans l\'ordre où elles sont écrites.',
            lignes: [
                'Pars de **20**, multiplie par 3 : 20 × 3 = **60**.',
                'Chaque étape repart du résultat de la précédente, jamais du nombre de départ.',
            ],
            svg: SVG_PROGRAMME,
        },
        {
            titre: 'Aller jusqu\'au bout',
            court: 'La réponse est le résultat de la dernière consigne, pas d\'une étape du milieu.',
            lignes: [
                '60 puis 66 sont des étapes. La réponse est 66 ÷ 2 = **33**.',
                'Je compte les consignes, et je vérifie que j\'en ai fait autant.',
            ],
            svg: SVG_JUSQU_AU_BOUT,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 3 — Remettre les étapes dans l'ordre
// ═══════════════════════════════════════════════════════════

const BTN_E = 'cp902rejouerE', BTN_F = 'cp902rejouerF';

/**
 * Le critère de la PREMIÈRE étape : ses deux nombres sont écrits dans le calcul.
 * C'est volontairement ce critère-là qui est enseigné, et pas « la plus petite
 * étiquette d'abord » — le générateur fait justement tourner deux chaînes que la
 * division fait redescendre, sur lesquelles le tri par taille échoue.
 */
const SVG_PREMIERE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" width="290" height="194" role="img"
     aria-label="Le calcul K égale parenthèse 24 plus 16 fermante fois 3 plus 7. Les nombres écrits dans le calcul sont 24, 16, 3 et 7. Trois étiquettes sont examinées. 24 plus 16 égale 40 : ses deux nombres sont écrits, elle peut commencer, coche verte. 40 fois 3 égale 120 : le 40 n'est écrit nulle part, croix rouge. 120 plus 7 égale 127 : le 120 n'est écrit nulle part, croix rouge. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Quelle étape en premier ?')}
  ${bouton(BTN_E, 210, 4)}
  ${etape(0.05, BTN_E,
    `<text x="14" y="48" font-size="13.5" font-weight="bold" fill="${ENCRE}">K = (<tspan fill="${BLEU}">24</tspan> + <tspan fill="${BLEU}">16</tspan>) × <tspan fill="${BLEU}">3</tspan> + <tspan fill="${BLEU}">7</tspan></text>`)}
  ${etape(0.16, BTN_E,
    `<text x="14" y="66" font-size="9.5" font-weight="bold" fill="${BLEU}">nombres écrits : 24, 16, 3, 7</text>`)}
  ${etape(0.30, BTN_E,
    bande(78) +
    `<text x="26" y="100" font-size="12.5" font-weight="bold" fill="${VERT}">24 + 16 = 40</text>` + coche(130, 101) +
    `<text x="154" y="100" font-size="9.5" fill="${ARDOISE}">ses 2 nombres sont là</text>`)}
  ${etape(0.52, BTN_E,
    bande(120) +
    `<text x="26" y="142" font-size="12.5" font-weight="bold" fill="${GRIS}">40 × 3 = 120</text>` + croix(130, 143) +
    `<text x="154" y="142" font-size="9.5" fill="${ARDOISE}">le 40 n'est pas écrit</text>`)}
  ${etape(0.74, BTN_E,
    bande(162) +
    `<text x="26" y="184" font-size="12.5" font-weight="bold" fill="${GRIS}">120 + 7 = 127</text>` + croix(130, 185) +
    `<text x="154" y="184" font-size="9.5" fill="${ARDOISE}">le 120 n'est pas écrit</text>`)}
</svg>`.trim();

const SVG_CHAINE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 186" width="290" height="180" role="img"
     aria-label="Les trois étapes remises en chaîne. 24 plus 16 égale 40. Une flèche reporte le 40 vers l'étape suivante : 40 fois 3 égale 120. Une flèche reporte le 120 : 120 plus 7 égale 127. En bas, la conclusion : le résultat 127 ne resert nulle part, c'est donc la dernière étape. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Je reporte le résultat')}
  ${bouton(BTN_F, 210, 4)}
  ${etape(0.05, BTN_F,
    `<text x="44" y="52" font-size="13.5" font-weight="bold" fill="${ENCRE}">24 + 16 = <tspan fill="${ORANGE}">40</tspan></text>`)}
  ${etape(0.22, BTN_F, flecheV(30, 58, 84) +
    `<text x="40" y="76" font-size="9.5" font-weight="bold" fill="${ORANGE}">je reporte 40</text>`)}
  ${etape(0.38, BTN_F,
    `<text x="44" y="98" font-size="13.5" font-weight="bold" fill="${ENCRE}"><tspan fill="${ORANGE}">40</tspan> × 3 = <tspan fill="${ORANGE}">120</tspan></text>`)}
  ${etape(0.54, BTN_F, flecheV(30, 104, 130) +
    `<text x="40" y="122" font-size="9.5" font-weight="bold" fill="${ORANGE}">je reporte 120</text>`)}
  ${etape(0.70, BTN_F,
    `<text x="44" y="144" font-size="13.5" font-weight="bold" fill="${ENCRE}"><tspan fill="${ORANGE}">120</tspan> + 7 = <tspan fill="${VERT}">127</tspan></text>` + coche(176, 145))}
  ${etape(0.86, BTN_F,
    `<text x="14" y="174" font-size="10" fill="${GRIS}">127 ne resert nulle part : c'est la dernière étape</text>`)}
</svg>`.trim();

const FICHE_ORDONNER = {
    pages: [
        {
            titre: 'Quelle étape en premier ?',
            court: 'La première étape n\'utilise que des nombres écrits dans le calcul.',
            lignes: [
                '**K = (24 + 16) × 3 + 7** : seul 24 + 16 n\'a besoin de rien d\'autre.',
                '40 × 3 ne peut pas commencer : le 40 n\'est écrit nulle part.',
            ],
            svg: SVG_PREMIERE,
        },
        {
            titre: 'Chaque étape reprend la précédente',
            court: 'Le résultat d\'une étape est un nombre de l\'étape suivante.',
            lignes: [
                '24 + 16 = **40**, donc l\'étape d\'après contient 40 : c\'est 40 × 3 = **120**.',
                'La dernière étape est celle dont le résultat ne resert plus.',
            ],
            svg: SVG_CHAINE,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 4 — Vérifier les étapes d'un calcul
// ═══════════════════════════════════════════════════════════

const BTN_G = 'cp902rejouerG', BTN_H = 'cp902rejouerH';

/**
 * L'expression est découpée en morceaux posés à des x FIXES, pour que les
 * soulignements verts tombent exactement sous les deux calculs prêts. Le piège
 * « 2 + 30 » est traité à part, en bas : ces deux nombres sont voisins à l'œil
 * mais appartiennent chacun à une opération plus prioritaire.
 */
const SVG_COMMENCER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 186" width="290" height="180" role="img"
     aria-label="Le calcul K égale parenthèse 18 moins 6 fermante fois 2 plus 42 divisé par 6. Deux morceaux sont soulignés en vert : 18 moins 6, et 42 divisé par 6. Ils n'attendent rien, on peut commencer par l'un ou par l'autre, deux coches vertes. En bas, le piège, croix rouge : 2 plus 42 est interdit, car le 2 appartient à la multiplication et le 42 à la division, ils sont seulement voisins à l'écrit. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Par quoi je peux commencer ?')}
  ${bouton(BTN_G, 210, 4)}
  ${etape(0.05, BTN_G,
    `<text x="14" y="52" font-size="13" font-weight="bold" fill="${ENCRE}">K =</text>` +
    `<text x="42" y="52" font-size="13" font-weight="bold" fill="${VERT}">(18 − 6)</text>` +
    `<text x="106" y="52" font-size="13" font-weight="bold" fill="${ENCRE}">× 2 +</text>` +
    `<text x="156" y="52" font-size="13" font-weight="bold" fill="${VERT}">42 ÷ 6</text>`)}
  ${etape(0.22, BTN_G,
    `<line x1="42" y1="58" x2="102" y2="58" stroke="${VERT}" stroke-width="1.8"/>` + coche(64, 76))}
  ${etape(0.36, BTN_G,
    `<line x1="156" y1="58" x2="204" y2="58" stroke="${VERT}" stroke-width="1.8"/>` + coche(172, 76))}
  ${etape(0.50, BTN_G,
    `<text x="14" y="96" font-size="10" fill="${ARDOISE}">les deux sont prêts : je commence par celui que je veux</text>`)}
  ${etape(0.62, BTN_G, filet(112))}
  ${etape(0.70, BTN_G,
    croix(16, 140) + `<text x="38" y="140" font-size="13" font-weight="bold" fill="${ROUGE}">2 + 42</text>`)}
  ${etape(0.84, BTN_G,
    `<text x="38" y="158" font-size="9.5" fill="${ARDOISE}">le 2 appartient à la multiplication,</text>` +
    `<text x="38" y="174" font-size="9.5" fill="${ARDOISE}">le 42 à la division : ils sont juste voisins</text>`)}
</svg>`.trim();

/**
 * Mêmes nombres, une paire de parenthèses en plus, et la dernière opération
 * change. C'est le contraste qui enseigne, pas chaque scène prise seule.
 */
const SVG_DERNIERE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 178" width="290" height="172" role="img"
     aria-label="Deux calculs faits des mêmes nombres. Le premier, 5 fois 3 plus 8 : la multiplication passe avant, on calcule 5 fois 3 égale 15 puis 15 plus 8 égale 23, la dernière opération est donc l'addition. Le second, 5 fois parenthèse 3 plus 8 : la parenthèse passe avant, on calcule 3 plus 8 égale 11 puis 5 fois 11 égale 55, la dernière opération est donc la multiplication. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('La dernière opération')}
  ${bouton(BTN_H, 210, 4)}
  ${etape(0.05, BTN_H,
    `<text x="20" y="52" font-size="14" font-weight="bold" fill="${ARDOISE}">5 × 3</text>` +
    `<text x="62" y="52" font-size="16" font-weight="bold" fill="${ORANGE}">+</text>` +
    `<text x="78" y="52" font-size="14" font-weight="bold" fill="${ARDOISE}">8</text>`)}
  ${etape(0.18, BTN_H,
    `<text x="106" y="52" font-size="11" font-weight="bold" fill="${ORANGE}">l'addition en dernier</text>`)}
  ${etape(0.30, BTN_H,
    `<text x="20" y="72" font-size="10" fill="${GRIS}">5 × 3 = 15, puis 15 + 8 = 23</text>`)}
  ${etape(0.42, BTN_H, filet(92))}
  ${etape(0.52, BTN_H,
    `<text x="20" y="132" font-size="14" font-weight="bold" fill="${ARDOISE}">5</text>` +
    `<text x="34" y="132" font-size="16" font-weight="bold" fill="${ORANGE}">×</text>` +
    `<text x="52" y="132" font-size="14" font-weight="bold" fill="${ARDOISE}">(3 + 8)</text>`)}
  ${etape(0.66, BTN_H,
    `<text x="118" y="132" font-size="11" font-weight="bold" fill="${ORANGE}">la multiplication</text>`)}
  ${etape(0.78, BTN_H,
    `<text x="20" y="152" font-size="10" fill="${GRIS}">3 + 8 = 11, puis 5 × 11 = 55</text>`)}
  ${etape(0.88, BTN_H,
    `<text x="20" y="172" font-size="10" fill="${BLEU}">une parenthèse en plus, et l'ordre s'inverse</text>`)}
</svg>`.trim();

/** Page commune au V/F et au QCM : repérer l'opération de tête. */
const PAGE_DERNIERE = {
    titre: 'La dernière opération',
    court: 'La dernière opération est celle de plus faible priorité, hors parenthèses.',
    lignes: [
        '**5 × 3 + 8** : le × passe avant, la dernière opération est l\'**addition**.',
        '**5 × (3 + 8)** : la parenthèse passe avant, la dernière est la **multiplication**.',
    ],
    svg: SVG_DERNIERE,
};

/** Le QCM ne pose que cette question : une seule page. */
const FICHE_VERIFIER_QCM = { pages: [PAGE_DERNIERE] };

const FICHE_VERIFIER_VF = {
    pages: [
        {
            titre: 'Par quoi je peux commencer',
            court: 'Je peux commencer par n\'importe quel calcul qui n\'attend rien d\'autre.',
            lignes: [
                '**K = (18 − 6) × 2 + 42 ÷ 6** : 18 − 6 et 42 ÷ 6 sont prêts tous les deux.',
                'En revanche 2 + 42 est interdit : ces deux nombres sont seulement voisins.',
            ],
            svg: SVG_COMMENCER,
        },
        PAGE_DERNIERE,
    ],
};

const CP_902 = {
    // Carte « effectuer » : la saisie s'arrête au résultat, le texte à trous
    // doit en plus tenir la réécriture. PAGE_PRIORITES est le seul objet commun.
    effectuer_texte:             FICHE_EFFECTUER_SAISIE,
    effectuer_texte_trou_saisie: FICHE_EFFECTUER_TROUS,
    // Carte « programme » : même parcours dans les deux formats, fiche entière
    // partagée (le QCM n'affiche que le nombre final, mais il faut l'avoir fait).
    programme_texte_trou_saisie: FICHE_PROGRAMME,
    programme_qcm_unique:        FICHE_PROGRAMME,
    etapes_ordonner:             FICHE_ORDONNER,
    // Carte « vérifier » : PAGE_DERNIERE est commune, le V/F y ajoute en page 1
    // « par quoi commencer », question que le QCM ne pose pas.
    verifier_tableau_vf:         FICHE_VERIFIER_VF,
    verifier_qcm_unique:         FICHE_VERIFIER_QCM,
};

export default CP_902;
